/* ============================================
   AccountSwitcher — Cambia entre cuentas (membresías)
   estilo Cloudflare. Se muestra solo si la identidad
   tiene más de una cuenta. Re-emite el JWT y redirige
   al panel del rol de la cuenta elegida.

   Es el ÚNICO selector de empresa del portal: antes convivía con un `<select>`
   propio en el sidebar, alimentado por `contacto_empresa` en vez de por las
   membresías. Los dos cambiaban de empresa, pero solo este re-emitía el token,
   así que podían quedar apuntando a empresas distintas al mismo tiempo — y como
   los endpoints del portal se scopean por el token, la pantalla mezclaba datos
   de dos empresas. Por eso acá se unen las dos fuentes en una sola lista.
   ============================================ */

import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, type UserRole } from '@/hooks/useAuth'
import { usePortalCliente } from '@/hooks/usePortalCliente'

function panelForRole(role: UserRole): string {
  if (role === 'admin' || role === 'ventas') return '/'
  if (role === 'partner') return '/partner/pipeline'
  return '/portal'
}

const roleLabel: Record<string, string> = { cliente: 'Cliente', partner: 'Partner', admin: 'Admin', ventas: 'Ventas' }

interface Cuenta { cliente_id: number; empresa?: string | null; role: UserRole }

export default function AccountSwitcher({ variant = 'light' }: { variant?: 'light' | 'dark' }) {
  const { accounts, clienteId, switchAccount } = useAuth()
  // Fuera del portal no hay provider: el contexto devuelve [] y esto es un no-op.
  const { afiliaciones } = usePortalCliente()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Unión membresías ∪ afiliaciones. `membresias` y `contacto_empresa` no están
  // siempre sincronizadas y switch-account acepta las dos (auth.routes.ts), así
  // que listar solo una dejaba empresas alcanzables fuera del switcher.
  const cuentas = useMemo<Cuenta[]>(() => {
    const porCliente = new Map<number, Cuenta>()
    for (const a of accounts) porCliente.set(a.cliente_id, a as Cuenta)
    for (const af of afiliaciones) {
      if (porCliente.has(af.cliente_id)) continue
      porCliente.set(af.cliente_id, { cliente_id: af.cliente_id, empresa: af.empresa_nombre, role: 'cliente' })
    }
    return [...porCliente.values()]
  }, [accounts, afiliaciones])

  if (cuentas.length < 2) return null

  const active = cuentas.find(a => a.cliente_id === clienteId)
  const activeLabel = active?.empresa || 'Cuenta'

  async function pick(clienteIdTarget: number, role: UserRole) {
    if (clienteIdTarget === clienteId) { setOpen(false); return }
    setSwitching(true)
    try {
      const newRole = await switchAccount(clienteIdTarget)
      setOpen(false)
      navigate(panelForRole(newRole || role), { replace: true })
    } catch {
      /* noop — el usuario puede reintentar */
    } finally {
      setSwitching(false)
    }
  }

  return (
    <div ref={ref} className={`acct-switch ${variant === 'dark' ? 'acct-switch--dark' : ''}`}>
      <button
        type="button"
        className="acct-switch__trigger"
        onClick={() => setOpen(o => !o)}
        disabled={switching}
      >
        <span className="acct-switch__avatar">{activeLabel.charAt(0).toUpperCase()}</span>
        <span className="acct-switch__name">{activeLabel}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
      </button>

      {open && (
        <div className="acct-switch__menu">
          <div className="acct-switch__menu-title">Cambiar de cuenta</div>
          {cuentas.map(a => {
            const activeItem = a.cliente_id === clienteId
            return (
              <button
                key={a.cliente_id}
                type="button"
                className={`acct-switch__item ${activeItem ? 'acct-switch__item--active' : ''}`}
                onClick={() => pick(a.cliente_id, a.role)}
              >
                <span className="acct-switch__item-name">
                  {a.empresa || `Empresa #${a.cliente_id}`}
                </span>
                <span className={`acct-switch__role acct-switch__role--${a.role}`}>
                  {roleLabel[a.role] || a.role}
                </span>
                {activeItem && (
                  <svg className="acct-switch__check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
